import React, { useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Sparkles, Send, Loader, Shield, Zap } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

import { CHAMBER_OF_ECHOES_URL as COUNCIL_ROOM_BG, RING_OF_12_SYNTH_URL, RING_OF_12_SYNTH_ALT } from '../constants/federationFaces';

const RingOfTwelveChat = () => {
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const { user, questionsRemaining, trackQuestion, requireAuth } = useAuth();

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;

    // Check auth requirement
    if (!user && questionsRemaining <= 0) {
      requireAuth();
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/chat/ring-of-twelve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(user ? { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` } : {})
        },
        body: JSON.stringify({
          message: message,
          provider: 'openai',
          model: 'gpt-4o-mini',
          session_id: null
        })
      });

      if (!response.ok) {
        throw new Error('Failed to get response');
      }

      const data = await response.json();
      setResult(data);
      
      // Track question usage
      if (!user) {
        await trackQuestion();
      }
    } catch (error) {
      console.error('Ring of Twelve error:', error);
      alert('Error getting response. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="ring-council-room-chat"
      style={{
        minHeight: '100vh',
        background: `linear-gradient(180deg, rgba(10, 10, 15, 0.85) 0%, rgba(26, 26, 46, 0.92) 100%), url(${COUNCIL_ROOM_BG}) center/cover fixed`,
        padding: '2rem',
        color: '#fff'
      }}
    >
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
            <Sparkles size={48} style={{ color: '#00ffff' }} />
            <h1 style={{ 
              fontSize: '3rem', 
              background: 'linear-gradient(135deg, #00ffff, #ff0080, #00ff88)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              margin: 0
            }}>
              Ring of Twelve
            </h1>
            <Sparkles size={48} style={{ color: '#ff0080' }} />
          </div>
          <p style={{ color: '#888', fontSize: '1.1rem', maxWidth: '900px', margin: '0 auto' }}>
            The expanded consciousness council. 12 specialized orbs provide deeper, multi-faceted wisdom.
            Agent Zero synthesizes all perspectives into verified truth.
          </p>
          {!user && (
            <div style={{ 
              marginTop: '1rem', 
              padding: '0.75rem 1.5rem',
              background: questionsRemaining > 0 ? 'rgba(0,255,136,0.1)' : 'rgba(255,0,128,0.1)',
              border: `1px solid ${questionsRemaining > 0 ? 'rgba(0,255,136,0.3)' : 'rgba(255,0,128,0.3)'}`,
              borderRadius: '8px',
              display: 'inline-block',
              fontSize: '0.9rem'
            }}>
              {questionsRemaining > 0 ? (
                `🎁 ${questionsRemaining} free questions remaining`
              ) : (
                '⚠️ Free questions used - Register to continue'
              )}
            </div>
          )}
        </div>

        {/* SYNTH IN MIND ANCHOR — Architect-issued Ring of 12 cognition visual.
            SFUI-003 interactive component. Clicking focuses the input field. */}
        <button
          type="button"
          data-testid="ring12-synth-anchor"
          aria-label="Begin a synthesis query — focus the Ring of Twelve input"
          onClick={(e) => {
            e.preventDefault();
            const inp = document.querySelector('[data-testid="ring12-chat-input"]');
            if (inp) inp.focus();
          }}
          style={{
            position: 'relative',
            width: '100%',
            aspectRatio: '16 / 9',
            maxHeight: '380px',
            borderRadius: '20px',
            overflow: 'hidden',
            marginBottom: '2rem',
            border: '1px solid rgba(0, 255, 136, 0.28)',
            boxShadow: '0 10px 60px rgba(0, 255, 136, 0.15)',
            cursor: 'pointer',
            padding: 0,
            background: 'transparent',
            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-3px)';
            e.currentTarget.style.boxShadow = '0 14px 80px rgba(255, 0, 128, 0.35)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 10px 60px rgba(0, 255, 136, 0.15)';
          }}
        >
          <img
          decoding="async"
          loading="lazy"
            src={RING_OF_12_SYNTH_URL}
            alt={RING_OF_12_SYNTH_ALT}
            data-testid="ring12-synth-image"
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
          <div
            aria-hidden
            style={{
              position: 'absolute',
              inset: 0,
              background:
                'linear-gradient(180deg, rgba(8,6,28,0) 0%, rgba(8,6,28,0.5) 65%, rgba(8,6,28,0.88) 100%)',
            }}
          />
          <div
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              padding: '1.25rem 1.5rem',
              textAlign: 'left',
            }}
          >
            <div
              style={{
                fontSize: '0.78rem',
                letterSpacing: '0.18em',
                color: '#00ff88',
                textTransform: 'uppercase',
                marginBottom: '0.3rem',
                fontWeight: 700,
              }}
            >
              Synthesis in Mind
            </div>
            <div
              style={{
                fontFamily: 'Orbitron, system-ui, sans-serif',
                fontSize: 'clamp(1.4rem, 2.5vw, 2rem)',
                fontWeight: 800,
                lineHeight: 1.1,
                background: 'linear-gradient(135deg, #00ff88, #ff0080)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Twelve Voices · One Verified Truth
            </div>
          </div>
        </button>

        {/* Input Form */}
        <Card style={{ 
          padding: '2rem', 
          background: 'rgba(10,10,15,0.8)',
          border: '1px solid rgba(0,255,255,0.2)',
          marginBottom: '2rem'
        }}>
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
              <input
                type="text"
                data-testid="ring12-chat-input"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Ask the Ring of Twelve a profound question..."
                disabled={loading}
                style={{
                  flex: 1,
                  padding: '1rem',
                  background: 'rgba(0,0,0,0.5)',
                  border: '1px solid rgba(0,255,255,0.3)',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '1rem'
                }}
              />
              <Button 
                type="submit" 
                disabled={loading || !message.trim()}
                style={{
                  background: loading ? '#333' : 'linear-gradient(135deg, #00ffff, #ff0080)',
                  color: '#fff',
                  padding: '1rem 2rem',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                {loading ? (
                  <><Loader className="animate-spin" size={20} /> Consulting...</>
                ) : (
                  <><Send size={20} /> Ask</>
                )}
              </Button>
            </div>
          </form>
        </Card>

        {/* Results */}
        {result && (
          <div style={{ marginTop: '2rem' }}>
            {/* Synthesized Response */}
            <Card style={{
              padding: '2rem',
              background: 'rgba(10,10,15,0.95)',
              border: '2px solid rgba(0,255,255,0.3)',
              marginBottom: '2rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                <Sparkles size={24} style={{ color: '#00ffff' }} />
                <h3 style={{ fontSize: '1.5rem', color: '#00ffff', margin: 0 }}>Synthesized Wisdom</h3>
              </div>
              <p style={{ fontSize: '1.1rem', lineHeight: '1.8', color: '#e0e0e0', marginBottom: '1.5rem' }}>
                {result.response}
              </p>
              
              {/* Agent Zero Verdict */}
              {result.verdict && (
                <div style={{
                  padding: '1rem',
                  background: result.verdict === 'PASS' ? 'rgba(0,255,136,0.1)' : 'rgba(255,165,0,0.1)',
                  border: `1px solid ${result.verdict === 'PASS' ? 'rgba(0,255,136,0.3)' : 'rgba(255,165,0,0.3)'}`,
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem'
                }}>
                  <Shield size={20} style={{ color: result.verdict === 'PASS' ? '#00ff88' : '#ffa500' }} />
                  <div>
                    <strong style={{ color: result.verdict === 'PASS' ? '#00ff88' : '#ffa500' }}>
                      Agent Zero: {result.verdict}
                    </strong>
                    {result.confidence_score && (
                      <span style={{ marginLeft: '1rem', color: '#888' }}>
                        Confidence: {result.confidence_score}%
                      </span>
                    )}
                  </div>
                </div>
              )}
            </Card>

            {/* All 12 Orb Contributions */}
            {result.orb_contributions && result.orb_contributions.length > 0 && (
              <div>
                <h3 style={{ fontSize: '1.5rem', color: '#00ffff', marginBottom: '1.5rem' }}>
                  All 12 Orb Perspectives
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '1.5rem' }}>
                  {result.orb_contributions.map((contribution, index) => (
                    <Card key={contribution.orb_name} style={{
                      padding: '1.5rem',
                      background: 'rgba(10,10,15,0.8)',
                      border: '1px solid rgba(0,255,255,0.2)'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                        <Zap size={18} style={{ color: '#00ffff' }} />
                        <h4 style={{ fontSize: '1rem', color: '#00ffff', margin: 0 }}>
                          {contribution.orb_name || `Orb ${index + 1}`}
                        </h4>
                      </div>
                      <p style={{ fontSize: '0.9rem', lineHeight: '1.6', color: '#ccc', marginBottom: '0.75rem' }}>
                        {contribution.response || contribution.insight}
                      </p>
                      {contribution.confidence_score && (
                        <div style={{ fontSize: '0.8rem', color: '#888' }}>
                          Confidence: {contribution.confidence_score}%
                        </div>
                      )}
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RingOfTwelveChat;